import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Award, AlertTriangle, Clock, CheckCircle, Download } from 'lucide-react';
import api from '../api';

const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

const STATUS_META = {
  expired: { label: 'Expired', color: '#b91c1c', bg: '#fee2e2', icon: AlertTriangle },
  due_soon: { label: 'Due Soon', color: '#b45309', bg: '#fef3c7', icon: Clock },
  current: { label: 'Current', color: '#15803d', bg: '#dcfce7', icon: CheckCircle },
};

// Admin → CMA Renewals
// 2-year recertification tracker for every CMA graduate.
const CMARenewalsPanel = () => {
  const [data, setData] = useState({ total: 0, counts: {}, entries: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [renewing, setRenewing] = useState({});

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/online-courses/cma-renewals');
      setData(res.data || { total: 0, counts: {}, entries: [] });
    } catch (_e) {
      setData({ total: 0, counts: {}, entries: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (data.entries || [])
      .filter((e) => filter === 'all' || e.status === filter)
      .filter((e) =>
        !n
          ? true
          : (e.user_name || '').toLowerCase().includes(n) ||
            (e.user_email || '').toLowerCase().includes(n) ||
            (e.verification_code || '').toLowerCase().includes(n)
      );
  }, [data.entries, q, filter]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }); }
    catch (_e) { return iso; }
  };

  const handleRenew = async (e) => {
    if (!window.confirm(
      `Issue a 2-year CMA Recertification for ${e.user_name}?\n\n` +
      `This resets their validity window to start TODAY and increments their renewal counter ` +
      `(currently #${e.renewal_count}). The renewal certificate will be available immediately.`
    )) return;
    setRenewing((s) => ({ ...s, [e.user_id]: true }));
    try {
      const res = await api.post(`/admin/online-courses/cma-renew/${e.user_id}`);
      alert(res.data?.message || 'Renewal issued.');
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not issue renewal.');
    } finally {
      setRenewing((s) => { const c = { ...s }; delete c[e.user_id]; return c; });
    }
  };

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';
  const handleDownload = async (e) => {
    try {
      const res = await fetch(
        `${API_HOST}/api/online-courses/cna-prep/certificate/${e.user_id}?_=${Date.now()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) { alert('Could not load certificate. Please try again.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (_err) {
      alert('Could not load certificate.');
    }
  };

  const counts = data.counts || {};

  return (
    <div data-testid="cma-renewals-panel">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <RefreshCw size={22} color="#0d9488" />
          CMA Recertification Tracker
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9375rem', marginTop: 6, marginBottom: 0 }}>
          Every CMA graduate must recertify on a 2-year cycle. Use this panel
          to spot graduates whose validity window is expiring and issue a
          one-click renewal certificate.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        {['expired', 'due_soon', 'current'].map((s) => {
          const meta = STATUS_META[s];
          const Icon = meta.icon;
          const active = filter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(active ? 'all' : s)}
              data-testid={`cma-renewal-card-${s}`}
              style={{
                background: meta.bg,
                color: meta.color,
                padding: '14px 18px',
                borderRadius: 10,
                minWidth: 170,
                borderLeft: `4px solid ${meta.color}`,
                border: active ? `2px solid ${meta.color}` : `1px solid ${meta.bg}`,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon size={14} />
                {meta.label}
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, fontFamily: 'Georgia, serif', marginTop: 4 }}>
                {counts[s] || 0}
              </div>
            </button>
          );
        })}
        {filter !== 'all' && (
          <button
            type="button"
            onClick={() => setFilter('all')}
            style={{ alignSelf: 'center', background: 'transparent', border: '1px dashed #94a3b8', color: '#475569', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}
            data-testid="cma-renewal-clear-filter"
          >
            Clear filter
          </button>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <input
          type="text"
          placeholder="Search by name, email, or verification code…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="form-input"
          style={{ width: '100%', maxWidth: 520 }}
          data-testid="cma-renewal-search"
        />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading renewals…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state" style={{ padding: 48 }}>
          <div className="empty-state-icon"><Award size={42} color="#0d9488" /></div>
          <h3>No matching CMA graduates</h3>
          <p style={{ color: '#64748b' }}>
            Issue a CMA certificate from Student Transcripts to populate the recertification tracker.
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table" data-testid="cma-renewals-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Graduate</th>
                  <th>Verification Code</th>
                  <th>Valid Through</th>
                  <th style={{ textAlign: 'center' }}>Days Left</th>
                  <th style={{ textAlign: 'center' }}>Renewals</th>
                  <th style={{ textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const meta = STATUS_META[e.status] || STATUS_META.current;
                  const Icon = meta.icon;
                  return (
                    <tr key={`${e.user_id}-cma`} data-testid={`cma-renewal-row-${e.user_id}`}>
                      <td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: meta.bg,
                            color: meta.color,
                            padding: '3px 9px',
                            borderRadius: 999,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                          }}
                        >
                          <Icon size={11} />
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {e.user_name}
                        {e.user_email && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 400 }}>
                            {e.user_email}
                          </div>
                        )}
                      </td>
                      <td style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: '0.78rem', color: '#475569' }}>
                        {e.verification_code}
                      </td>
                      <td>{fmtDate(e.renewal_expires_at)}</td>
                      <td style={{ textAlign: 'center', color: e.status === 'expired' ? '#b91c1c' : (e.status === 'due_soon' ? '#b45309' : '#0f172a'), fontWeight: 700 }}>
                        {e.days_until_renewal >= 0 ? e.days_until_renewal : `${e.days_until_renewal}`}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {e.renewal_count > 0 ? (
                          <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700 }}>
                            #{e.renewal_count}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={() => handleRenew(e)}
                          disabled={!!renewing[e.user_id]}
                          data-testid={`cma-renew-${e.user_id}`}
                          style={{ padding: '5px 10px', background: 'linear-gradient(135deg,#0d9488,#065f46)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <RefreshCw size={12} />
                          {renewing[e.user_id] ? 'Renewing…' : 'Renew'}
                        </button>
                        {' '}
                        <button
                          type="button"
                          onClick={() => handleDownload(e)}
                          data-testid={`cma-download-${e.user_id}`}
                          style={{ padding: '5px 10px', background: '#0b2545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          title="Download current certificate"
                        >
                          <Download size={12} />
                          Cert
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMARenewalsPanel;
